import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { createAppThunk } from '../createAppThunk';
import type {
  CreateFirstAidLogDto,
  EnrichedActivityLog,
  FirstAidLog,
  GetAllLogsData,
  UpdateFirstAidLogDto,
} from '@/types/Api';
import {
  createFirstAidLog as createFirstAidLogApi,
  deleteFirstAidLog as deleteFirstAidLogApi,
  fetchActivityLogsByEntityId,
  fetchFirstAidLogById as fetchFirstAidLogByIdApi,
  fetchFirstAidLogsByOrg as fetchFirstAidLogsByOrgApi,
  updateFirstAidLogApi,
} from '@/lib/apiClient';
import type { NormalizedError } from '@/types/NormalizedError';
import { toAppError } from '@/lib/toAppError';
import { OPTIMISTIC_ACTIVITY_LOG_TTL_MS } from '@/redux/optimisticActivityLog';

interface FirstAidLogState {
  logs: FirstAidLog[];
  currentLog: FirstAidLog | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  fetchStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  fetchOneStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  updateStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  deleteStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: NormalizedError | null;
  fetchError: NormalizedError | null;
  fetchOneError: NormalizedError | null;
  updateError: NormalizedError | null;
  deleteError: NormalizedError | null;
  currentLogActivityLogs: {
    logs: GetAllLogsData;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error: NormalizedError | null;
  };
  pendingOptimisticCurrentLogActivityLogs: EnrichedActivityLog[];
}

const prunePendingOptimisticCurrentLogActivityLogs = (
  optimisticLogs: EnrichedActivityLog[],
): EnrichedActivityLog[] => {
  const now = Date.now();
  return optimisticLogs.filter((entry) => {
    const timestamp = new Date(entry.timestamp).getTime();
    return Number.isFinite(timestamp) && now - timestamp <= OPTIMISTIC_ACTIVITY_LOG_TTL_MS;
  });
};

const mergeCurrentLogActivityLogs = (
  serverLogs: GetAllLogsData,
  optimisticLogs: EnrichedActivityLog[],
): GetAllLogsData => {
  const activeOptimisticLogs = prunePendingOptimisticCurrentLogActivityLogs(optimisticLogs);
  const optimisticIds = new Set(activeOptimisticLogs.map((entry) => entry.id));
  return [...activeOptimisticLogs, ...serverLogs.filter((log) => !optimisticIds.has(log.id))].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  );
};

const initialState: FirstAidLogState = {
  logs: [],
  currentLog: null,
  status: 'idle',
  fetchStatus: 'idle',
  fetchOneStatus: 'idle',
  updateStatus: 'idle',
  deleteStatus: 'idle',
  error: null,
  fetchError: null,
  fetchOneError: null,
  updateError: null,
  deleteError: null,
  currentLogActivityLogs: {
    logs: [],
    status: 'idle',
    error: null,
  },
  pendingOptimisticCurrentLogActivityLogs: [],
};

export const createFirstAidLog = createAppThunk<
  FirstAidLog,
  { logData: CreateFirstAidLogDto; token: string; orgId: string }
>('firstAidLog/createFirstAidLog', async ({ logData, token, orgId }, thunkAPI) => {
  const { data, error } = await createFirstAidLogApi(orgId, logData, token);
  if (error) return thunkAPI.rejectWithValue(error);
  if (!data) return thunkAPI.rejectWithValue(toAppError(new Error('No data returned from API')));
  return data;
});

// New async thunk for fetching logs
export const fetchFirstAidLogsByOrg = createAppThunk<
  FirstAidLog[],
  { token: string; orgId: string }
>('firstAidLog/fetchFirstAidLogsByOrg', async ({ token, orgId }, thunkAPI) => {
  const { data, error } = await fetchFirstAidLogsByOrgApi(orgId, token);
  if (error) return thunkAPI.rejectWithValue(error);
  if (!data) return [];
  return data;
});

// Async thunk for fetching a single first-aid log by ID
export const fetchFirstAidLogById = createAppThunk<
  FirstAidLog,
  { token: string; orgId: string; logId: string }
>('firstAidLog/fetchFirstAidLogById', async ({ token, orgId, logId }, thunkAPI) => {
  const { data, error } = await fetchFirstAidLogByIdApi(orgId, logId, token);
  if (error) return thunkAPI.rejectWithValue(error);
  if (!data) return thunkAPI.rejectWithValue(toAppError(new Error('No data returned from API')));
  return data;
});

// Async thunk for updating a first-aid log by ID
export const updateFirstAidLog = createAppThunk<
  FirstAidLog,
  { token: string; orgId: string; logId: string; logData: UpdateFirstAidLogDto }
>('firstAidLog/updateFirstAidLog', async ({ token, orgId, logId, logData }, thunkAPI) => {
  const { data, error } = await updateFirstAidLogApi(orgId, logId, logData, token);
  if (error) return thunkAPI.rejectWithValue(error);
  if (!data)
    return thunkAPI.rejectWithValue(toAppError(new Error('Failed to update first aid log')));
  setTimeout(() => {
    void thunkAPI.dispatch(fetchFirstAidLogsActivityLogs({ token, orgId, logId }));
  }, 3000);
  return data;
});

// Async thunk for deleting a first-aid log by ID
export const deleteFirstAidLog = createAppThunk<
  string,
  { token: string; orgId: string; logId: string }
>('firstAidLog/deleteFirstAidLog', async ({ token, orgId, logId }, thunkAPI) => {
  const { error } = await deleteFirstAidLogApi(orgId, logId, token);
  if (error) return thunkAPI.rejectWithValue(error);
  return logId;
});

export const fetchFirstAidLogsActivityLogs = createAppThunk<
  GetAllLogsData,
  { token: string; orgId: string; logId: string }
>('firstAidLog/fetchFirstAidLogsActivityLogs', async ({ token, orgId, logId }, thunkAPI) => {
  const { data, error } = await fetchActivityLogsByEntityId(logId, orgId, token);
  if (error) return thunkAPI.rejectWithValue(error);
  return data || [];
});

export const firstAidLogSlice = createSlice({
  name: 'firstAidLog',
  initialState,
  reducers: {
    addOptimisticCurrentLogActivityLog: (state, action: PayloadAction<EnrichedActivityLog>) => {
      const optimisticLog = action.payload;
      state.pendingOptimisticCurrentLogActivityLogs = [
        optimisticLog,
        ...prunePendingOptimisticCurrentLogActivityLogs(
          state.pendingOptimisticCurrentLogActivityLogs,
        ).filter((log) => log.id !== optimisticLog.id),
      ];
      state.currentLogActivityLogs.logs = mergeCurrentLogActivityLogs(
        state.currentLogActivityLogs.logs,
        state.pendingOptimisticCurrentLogActivityLogs,
      );
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Log reducers
      .addCase(createFirstAidLog.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(createFirstAidLog.fulfilled, (state) => {
        state.status = 'succeeded';
        // Optionally add the new log to the state if needed immediately
      })
      .addCase(createFirstAidLog.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? null;
      })
      // Fetch Logs reducers
      .addCase(fetchFirstAidLogsByOrg.pending, (state) => {
        state.fetchStatus = 'loading';
        state.fetchError = null;
      })
      .addCase(fetchFirstAidLogsByOrg.fulfilled, (state, action: PayloadAction<FirstAidLog[]>) => {
        state.fetchStatus = 'succeeded';
        state.logs = action.payload;
      })
      .addCase(fetchFirstAidLogsByOrg.rejected, (state, action) => {
        state.fetchStatus = 'failed';
        state.fetchError = action.payload ?? null;
        state.logs = []; // Clear logs on error
      })
      // Fetch Single Log reducers
      .addCase(fetchFirstAidLogById.pending, (state) => {
        state.fetchOneStatus = 'loading';
        state.fetchOneError = null;
      })
      .addCase(fetchFirstAidLogById.fulfilled, (state, action: PayloadAction<FirstAidLog>) => {
        state.fetchOneStatus = 'succeeded';
        state.currentLog = action.payload;
      })
      .addCase(fetchFirstAidLogById.rejected, (state, action) => {
        state.fetchOneStatus = 'failed';
        state.fetchOneError = action.payload ?? null;
        state.currentLog = null;
      })
      // Fetch Single Log reducers
      .addCase(fetchFirstAidLogsActivityLogs.pending, (state) => {
        state.currentLogActivityLogs.status = 'loading';
        state.fetchOneError = null;
      })
      .addCase(
        fetchFirstAidLogsActivityLogs.fulfilled,
        (state, action: PayloadAction<GetAllLogsData>) => {
          state.currentLogActivityLogs.status = 'succeeded';
          state.pendingOptimisticCurrentLogActivityLogs =
            prunePendingOptimisticCurrentLogActivityLogs(
              state.pendingOptimisticCurrentLogActivityLogs,
            );
          state.currentLogActivityLogs.logs = mergeCurrentLogActivityLogs(
            action.payload,
            state.pendingOptimisticCurrentLogActivityLogs,
          );
        },
      )
      .addCase(fetchFirstAidLogsActivityLogs.rejected, (state, action) => {
        state.currentLogActivityLogs.status = 'failed';
        state.currentLogActivityLogs.error = action.payload ?? null;
        state.currentLogActivityLogs.logs = [];
        state.pendingOptimisticCurrentLogActivityLogs = [];
      })
      // Update Log reducers
      .addCase(updateFirstAidLog.pending, (state) => {
        state.updateStatus = 'loading';
        state.deleteError = null;
      })
      .addCase(updateFirstAidLog.fulfilled, (state, action: PayloadAction<FirstAidLog>) => {
        state.updateStatus = 'succeeded';
        // Remove the deleted log from the logs array
        state.logs = [action.payload, ...state.logs];
        if (state.currentLog?.id === action.payload.id) {
          state.currentLog = action.payload;
        }
      })
      .addCase(updateFirstAidLog.rejected, (state, action) => {
        state.updateStatus = 'failed';
        state.updateError = action.payload ?? null;
      })
      // Delete Log reducers
      .addCase(deleteFirstAidLog.pending, (state) => {
        state.deleteStatus = 'loading';
        state.deleteError = null;
      })
      .addCase(deleteFirstAidLog.fulfilled, (state, action: PayloadAction<string>) => {
        state.deleteStatus = 'succeeded';
        // Remove the deleted log from the logs array
        state.logs = state.logs.filter((log) => log.id !== action.payload);
        // Clear current log if it's the one being deleted
        if (state.currentLog?.id === action.payload) {
          state.currentLog = null;
        }
      })
      .addCase(deleteFirstAidLog.rejected, (state, action) => {
        state.deleteStatus = 'failed';
        state.deleteError = action.payload ?? null;
      });
  },
});

export const { addOptimisticCurrentLogActivityLog } = firstAidLogSlice.actions;

export default firstAidLogSlice.reducer;
